import {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ForwardedRef,
  type ImgHTMLAttributes,
} from 'react'
import { useWechatMediaEnvironment } from '../../hooks/useWechatMediaMode'
import styles from './ProtectedImage.module.css'

interface ProtectedImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  src: string
  wechatSrcSet?: string
}

interface ResponsiveCandidate {
  src: string
  width: number
}

function assignForwardedRef(
  ref: ForwardedRef<HTMLElement>,
  node: HTMLElement | null,
) {
  if (typeof ref === 'function') {
    ref(node)
  } else if (ref) {
    ref.current = node
  }
}

function parseSrcSet(srcSet?: string): ResponsiveCandidate[] {
  if (!srcSet) return []

  return srcSet
    .split(',')
    .map((candidate) => candidate.trim().match(/^(.*)\s+(\d+)w$/))
    .filter((match): match is RegExpMatchArray => Boolean(match))
    .map((match) => ({ src: match[1], width: Number(match[2]) }))
    .sort((a, b) => a.width - b.width)
}

function selectResponsiveSource(
  src: string,
  srcSet: string | undefined,
  element: HTMLElement,
) {
  const candidates = parseSrcSet(srcSet)
  if (candidates.length === 0) return src

  const renderedWidth = element.getBoundingClientRect().width || window.innerWidth
  const targetWidth = renderedWidth * Math.min(window.devicePixelRatio || 1, 2)

  return (
    candidates.find((candidate) => candidate.width >= targetWidth) ??
    candidates[candidates.length - 1]
  ).src
}

function mapObjectFitToBackgroundSize(objectFit: string) {
  if (objectFit === 'contain') return 'contain'
  if (objectFit === 'fill') return '100% 100%'
  if (objectFit === 'none') return 'auto'
  return 'cover'
}

export const ProtectedImage = forwardRef<HTMLElement, ProtectedImageProps>(
  function ProtectedImage(
    {
      alt,
      className,
      decoding,
      fetchPriority,
      height,
      loading,
      onError,
      onLoad,
      src,
      style,
      wechatSrcSet,
      width,
      ...imageProps
    },
    forwardedRef,
  ) {
    const { isWechat } = useWechatMediaEnvironment()
    const mediaRef = useRef<HTMLElement | null>(null)
    const [isNearViewport, setIsNearViewport] = useState(
      loading !== 'lazy' || fetchPriority === 'high',
    )
    const [backgroundSource, setBackgroundSource] = useState<string | null>(null)
    const [backgroundPresentation, setBackgroundPresentation] = useState({
      position: '50% 50%',
      size: 'cover',
    })

    const bindMedia = useCallback(
      (node: HTMLElement | null) => {
        mediaRef.current = node
        assignForwardedRef(forwardedRef, node)
      },
      [forwardedRef],
    )

    useLayoutEffect(() => {
      if (!isWechat) return
      const media = mediaRef.current
      if (!media) return

      const syncPresentation = () => {
        const computedStyle = window.getComputedStyle(media)
        const nextPresentation = {
          position: computedStyle.objectPosition || '50% 50%',
          size: mapObjectFitToBackgroundSize(computedStyle.objectFit),
        }

        setBackgroundPresentation((current) =>
          current.position === nextPresentation.position &&
          current.size === nextPresentation.size
            ? current
            : nextPresentation,
        )
      }

      syncPresentation()
      const resizeObserver = new ResizeObserver(syncPresentation)
      resizeObserver.observe(media)
      window.addEventListener('orientationchange', syncPresentation)

      return () => {
        resizeObserver.disconnect()
        window.removeEventListener('orientationchange', syncPresentation)
      }
    }, [isWechat])

    useEffect(() => {
      if (!isWechat || isNearViewport) return
      const media = mediaRef.current
      if (!media) return

      if (!('IntersectionObserver' in window)) {
        setIsNearViewport(true)
        return
      }

      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return
          setIsNearViewport(true)
          observer.disconnect()
        },
        { rootMargin: '100% 0px', threshold: 0.01 },
      )

      observer.observe(media)
      return () => observer.disconnect()
    }, [isNearViewport, isWechat])

    useEffect(() => {
      if (!isWechat || !isNearViewport) return
      const media = mediaRef.current
      if (!media) return

      let disposed = false
      const selectedSource = selectResponsiveSource(src, wechatSrcSet, media)
      const image = new Image()

      image.onload = () => {
        if (disposed) return
        setBackgroundSource(selectedSource)
        ;(onLoad as (() => void) | undefined)?.()
      }
      image.onerror = () => {
        if (!disposed) (onError as (() => void) | undefined)?.()
      }
      image.src = selectedSource

      return () => {
        disposed = true
        image.onload = null
        image.onerror = null
      }
    }, [isNearViewport, isWechat, onError, onLoad, src, wechatSrcSet])

    if (!isWechat) {
      return (
        <img
          {...imageProps}
          ref={bindMedia as ForwardedRef<HTMLImageElement>}
          className={className}
          src={src}
          alt={alt}
          width={width}
          height={height}
          loading={loading}
          decoding={decoding}
          fetchPriority={fetchPriority}
          style={style}
          draggable={false}
          onLoad={onLoad}
          onError={onError}
        />
      )
    }

    const aspectRatio =
      typeof width === 'number' && typeof height === 'number'
        ? `${width} / ${height}`
        : undefined
    const backgroundStyle: CSSProperties = {
      ...style,
      aspectRatio: style?.aspectRatio ?? aspectRatio,
      backgroundImage: backgroundSource
        ? `url(${JSON.stringify(backgroundSource)})`
        : 'none',
      backgroundPosition: backgroundPresentation.position,
      backgroundSize: backgroundPresentation.size,
    }
    const isDecorative = alt === '' || imageProps['aria-hidden'] === true

    return (
      <div
        ref={bindMedia as ForwardedRef<HTMLDivElement>}
        className={`${className ?? ''} ${styles.wechatProtectedMedia} protected-media`}
        role={isDecorative ? undefined : 'img'}
        aria-label={isDecorative ? undefined : alt}
        aria-hidden={isDecorative ? true : undefined}
        data-wechat-protected-media="true"
        style={backgroundStyle}
        onContextMenu={(event) => event.preventDefault()}
      />
    )
  },
)
